import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeAuthenticatedRequest } from "../utils/api";
import { decrypt, encrypt } from "../utils/cryptoUtils";
import { ForgotOtp } from "../redux/LoginSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";

const ForgetPassword = () => {
  const [username, setUsername] = useState("");
  const [errors, setErrors] = useState({ username: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { username: "" };
    let isValid = true;

    if (!username.trim()) {
      newErrors.username = "Username is required.";
      isValid = false;
    } else if (username.length < 3) {
      newErrors.username = "Username must be at least 3 characters.";
      isValid = false;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(username)) {
      newErrors.username = "Please enter a valid email address.";
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) return;

    setLoading(true);
    try {
      const jsonData = JSON.stringify({ email: username });
      const encrypted = encrypt(jsonData);

      const response = await makeAuthenticatedRequest(
        "POST",
        "/forgot/password/email",
        { encrypted },
        navigate
      );

      const decryptdata = decrypt(response);
      const jsonArray = JSON.parse(decryptdata.replace(/\\"/g, '"'));

      if (jsonArray?.Status === "200") {
        dispatch(ForgotOtp(jsonArray));
        toast.success("OTP Sent Successfully! Redirecting...");
        setTimeout(() => {
          navigate("/forgetotp");
        }, 1500);
      } else {
        toast.error(jsonArray.Err || "Unknown error");
      }
    } catch (Err) {
      console.error("Forgot Password Error:", Err.message);
      setError(Err.message);
      toast.error(Err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => navigate("/login");

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .forgot-root {
          min-height: 100vh;
          display: flex;
          font-family: 'Inter', sans-serif;
          background: #0f0f1a;
        }

        /* ── LEFT PANEL ── */
        .forgot-left {
          flex: 1;
          display: none;
          position: relative;
          overflow: hidden;
          background: linear-gradient(145deg, #0d0d1f 0%, #1a0a3d 50%, #0d1a3d 100%);
        }

        @media (min-width: 1024px) {
          .forgot-left { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 3rem; }
        }

        /* Animated mesh orbs */
        .forgot-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(85px);
          opacity: 0.5;
          pointer-events: none;
          animation: orbFloat 14s ease-in-out infinite;
        }
        .forgot-orb-1 {
          width: 450px; height: 450px;
          background: radial-gradient(circle, rgba(99,91,255,0.55) 0%, transparent 70%);
          top: -100px; left: -100px;
          animation-duration: 16s;
        }
        .forgot-orb-2 {
          width: 380px; height: 380px;
          background: radial-gradient(circle, rgba(139,92,246,0.45) 0%, transparent 70%);
          bottom: -80px; right: -80px;
          animation-duration: 20s;
          animation-direction: reverse;
        }

        @keyframes orbFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -45px) scale(1.06); }
        }

        /* Grid dots overlay */
        .forgot-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 28px 28px;
          pointer-events: none;
        }

        /* Left panel content */
        .left-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 440px;
        }

        .left-brand-icon {
          width: 72px; height: 72px;
          border-radius: 20px;
          background: linear-gradient(135deg, #635bff, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1.5rem;
          box-shadow: 0 12px 40px rgba(99,91,255,0.6), inset 0 1px 0 rgba(255,255,255,0.2);
        }

        .left-title {
          font-size: 2.5rem;
          font-weight: 800;
          line-height: 1.15;
          letter-spacing: -0.03em;
          background: linear-gradient(135deg, #fff 0%, #c4b5fd 60%, #818cf8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 1rem;
        }

        .left-subtitle {
          font-size: 0.95rem;
          color: rgba(148,163,184,0.8);
          line-height: 1.65;
          font-weight: 400;
          margin-bottom: 2.5rem;
        }

        /* Feature pills */
        .feature-pills {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          align-items: flex-start;
          width: 100%;
        }

        .feature-pill {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.25rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 1rem;
          width: 100%;
          backdrop-filter: blur(10px);
        }

        .feature-pill-icon {
          width: 36px; height: 36px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          font-size: 1.1rem;
        }

        .feature-pill-text strong {
          display: block;
          font-size: 0.82rem;
          font-weight: 600;
          color: #e2e8f0;
          margin-bottom: 1px;
        }
        .feature-pill-text span {
          font-size: 0.72rem;
          color: rgba(148,163,184,0.6);
        }

        /* ── RIGHT PANEL ── */
        .forgot-right {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 1.25rem;
          background: linear-gradient(160deg, #0f0f1a 0%, #13111f 100%);
          position: relative;
          overflow: hidden;
        }

        @media (min-width: 1024px) {
          .forgot-right { width: 480px; flex-shrink: 0; }
        }

        /* Right subtle glow */
        .forgot-right::before {
          content: '';
          position: absolute;
          top: -60px; right: -60px;
          width: 320px; height: 320px;
          background: radial-gradient(circle, rgba(99,91,255,0.08) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .forgot-card {
          width: 100%;
          max-width: 400px;
          position: relative;
          z-index: 2;
        }

        /* Top brand for mobile */
        .mobile-brand {
          text-align: center;
          margin-bottom: 2rem;
        }

        @media (min-width: 1024px) {
          .mobile-brand { display: none; }
        }

        .mobile-brand-icon {
          width: 52px; height: 52px;
          border-radius: 14px;
          background: linear-gradient(135deg, #635bff, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 0.75rem;
          box-shadow: 0 8px 24px rgba(99,91,255,0.5);
        }

        /* Heading */
        .forgot-heading {
          margin-bottom: 0.375rem;
        }

        .forgot-heading h1 {
          font-size: 1.75rem;
          font-weight: 800;
          color: #f1f5f9;
          letter-spacing: -0.025em;
          line-height: 1.2;
        }

        .forgot-heading p {
          font-size: 0.875rem;
          color: rgba(148,163,184,0.7);
          margin-top: 0.5rem;
          font-weight: 400;
          line-height: 1.5;
        }

        /* Divider */
        .forgot-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(99,91,255,0.3), transparent);
          margin: 1.5rem 0;
        }

        /* Form field */
        .forgot-field {
          margin-bottom: 1.5rem;
        }

        .forgot-field label {
          display: block;
          font-size: 0.76rem;
          font-weight: 600;
          color: rgba(148,163,184,0.8);
          letter-spacing: 0.04em;
          text-transform: uppercase;
          margin-bottom: 0.5rem;
        }

        .forgot-input-wrap {
          position: relative;
        }

        .forgot-input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(148,163,184,0.4);
          font-size: 1.05rem !important;
          pointer-events: none;
        }

        .forgot-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 0.875rem;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          font-size: 0.9rem;
          color: #f1f5f9;
          font-family: 'Inter', sans-serif;
          transition: all 0.25s ease;
          outline: none;
        }

        .forgot-input::placeholder { color: rgba(148,163,184,0.35); }

        .forgot-input:focus {
          border-color: rgba(99,91,255,0.6);
          background: rgba(99,91,255,0.06);
          box-shadow: 0 0 0 3px rgba(99,91,255,0.12);
        }

        .forgot-input.error {
          border-color: rgba(239,68,68,0.5);
          background: rgba(239,68,68,0.04);
        }

        .forgot-error-msg {
          font-size: 0.72rem;
          color: #f87171;
          margin-top: 0.375rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* Submit button */
        .forgot-submit-btn {
          width: 100%;
          padding: 0.9rem 1.5rem;
          background: linear-gradient(135deg, #635bff 0%, #7c3aed 100%);
          border: none;
          border-radius: 0.875rem;
          color: white;
          font-size: 0.9rem;
          font-weight: 700;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 8px 24px rgba(99,91,255,0.45), inset 0 1px 0 rgba(255,255,255,0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          letter-spacing: 0.01em;
          position: relative;
          overflow: hidden;
        }

        .forgot-submit-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .forgot-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 32px rgba(99,91,255,0.6), inset 0 1px 0 rgba(255,255,255,0.15);
        }

        .forgot-submit-btn:active:not(:disabled) { transform: translateY(0px); }

        .forgot-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Spinner */
        .btn-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        /* Register link */
        .forgot-back-row {
          text-align: center;
          margin-top: 1.75rem;
          font-size: 0.82rem;
          color: rgba(148, 163, 184, 0.55);
        }

        .forgot-back-row button {
          background: none;
          border: none;
          font-size: 0.82rem;
          font-weight: 700;
          color: #818cf8;
          cursor: pointer;
          margin-left: 0.25rem;
          font-family: 'Inter', sans-serif;
          transition: color 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .forgot-back-row button:hover { color: #a78bfa; }

        /* Security badge */
        .forgot-security-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          margin-top: 1.5rem;
          font-size: 0.7rem;
          color: rgba(148,163,184,0.35);
          font-weight: 500;
          letter-spacing: 0.03em;
        }
      `}</style>

      <div className="forgot-root">

        {/* ── LEFT PANEL ── */}
        <div className="forgot-left">
          <div className="forgot-grid" />
          <div className="forgot-orb forgot-orb-1" />
          <div className="forgot-orb forgot-orb-2" />

          <div className="left-content">
            <div className="left-brand-icon">
              <span className="material-icons" style={{ fontSize: "2rem", color: "white" }}>payments</span>
            </div>

            <div className="left-title">Gaadiaa<br />Trusted Payments</div>

            <p className="left-subtitle">
              Verify your security credentials to safely reset your password and keep your operations protected.
            </p>

            <div className="feature-pills">
              <div className="feature-pill">
                <div className="feature-pill-icon" style={{ background: "rgba(99,91,255,0.2)" }}>
                  <span className="material-icons" style={{ fontSize: "1.15rem", color: "#818cf8" }}>security</span>
                </div>
                <div className="feature-pill-text">
                  <strong>Secured Access Reset</strong>
                  <span>Bound dynamic security hooks for resets</span>
                </div>
              </div>

              <div className="feature-pill">
                <div className="feature-pill-icon" style={{ background: "rgba(16,185,129,0.15)" }}>
                  <span className="material-icons" style={{ fontSize: "1.15rem", color: "#34d399" }}>verified_user</span>
                </div>
                <div className="feature-pill-text">
                  <strong>Validation Integrity</strong>
                  <span>Phishing validation and secure token triggers</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="forgot-right">
          <div className="forgot-card">

            {/* Mobile-only brand */}
            <div className="mobile-brand">
              <div className="mobile-brand-icon">
                <span className="material-icons" style={{ fontSize: "1.5rem", color: "white" }}>payments</span>
              </div>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>Gaadiaa</div>
              <div style={{ fontSize: "0.7rem", color: "rgba(148,163,184,0.5)", fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: "2px" }}>Trusted Payments</div>
            </div>

            {/* Heading */}
            <div className="forgot-heading">
              <h1>Forgot password?</h1>
              <p>Enter your registered email address and we'll send a 6-digit verification code to reset your password.</p>
            </div>

            <div className="forgot-divider" />

            <form onSubmit={handleSubmit} autoComplete="off">

              {/* Registered Email */}
              <div className="forgot-field">
                <label>Registered Email Address <span style={{ color: "#f87171" }}>*</span></label>
                <div className="forgot-input-wrap">
                  <span className="material-icons forgot-input-icon">mail</span>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="name@company.com"
                    className={`forgot-input ${errors.username ? "error" : ""}`}
                  />
                </div>
                {errors.username && (
                  <p className="forgot-error-msg">
                    <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                    {errors.username}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="forgot-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner" />
                    Sending OTP…
                  </>
                ) : (
                  <>
                    Send Reset Code
                    <span className="material-icons" style={{ fontSize: "1.1rem" }}>arrow_forward</span>
                  </>
                )}
              </button>
            </form>

            {/* Back to Login link */}
            <div className="forgot-back-row">
              Remember your password?
              <button onClick={handleBackToLogin}>
                <span className="material-icons" style={{ fontSize: "1rem" }}>arrow_back</span>
                Back to Sign In
              </button>
            </div>

            {/* Security badge */}
            <div className="forgot-security-badge">
              <span className="material-icons" style={{ fontSize: "0.85rem" }}>lock</span>
              Secured reset binding protocol
            </div>

          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        toastStyle={{ background: "#1e1b4b", color: "#e2e8f0", border: "1px solid rgba(99,91,255,0.2)" }}
      />
    </>
  );
};

export default ForgetPassword;